/**
 * 将时间轴上的人脸检测帧合并为连续时间块（支持多人脸轨迹）
 *
 * ## 规则摘要
 * 1. **相似整合**：同轨迹相邻帧同时满足——**人脸中心** L∞ 偏移不超过 **动态阈值** `max(k√(w₁²+h₁²), k√(w₂²+h₂²))`（`k = faceCenterOffsetHalfDiagonal`，默认见 {@link DEFAULT_MERGE_FACE_TIMELINE_OPTIONS}；`w/h` 为归一宽高）。任一框宽高无效时对那一侧 **`k` 本身**兜底。且**框面积**相对变化 `|A1-A2|/max(A1,A2)` 不超过 `faceAreaChangeRatioMax`。
 * 2. **短片段**：时长 **&lt;** `shortSegmentMaxDurationSec` 的人脸块丢弃 `segments`；**仅当**与前一块**时间相接**时才延长前块 `endTime`，否则保留为独立 **`segments: []`** 块（中间无人脸区间不得被合并进前一段人脸）。
 * 3. **小脸过滤**：归一化框 **width** 小于 `minFaceWidth` 或 **height** 小于 `minFaceHeight` 的检测不参与轨迹。
 *
 * ## 输出
 * `TimelineBlock[]`：`{ startTime, endTime, segments }`，`segments` 长度 0～N；最后在首末帧时间范围内用 `segments: []` 填满间隙。
 *
 * @module SpeakerDetection/api/mergeFaceTimeline
 */

/**
 * @typedef {Object} FaceData
 * @property {number} x
 * @property {number} y
 * @property {number} width
 * @property {number} height
 * @property {number} mouthOpenness
 */

/**
 * @typedef {Object} FrameFaceRow
 * @property {FaceData[]} facesData
 * @property {number} currentTime
 */

/**
 * @typedef {Object} MergedFaceSegment
 * @property {FaceData} faceData
 * @property {number} startTime
 * @property {number} endTime
 * @property {number} frameCount
 * @property {number} trackId
 */

/**
 * @typedef {Object} TimelineFaceSegment
 * @property {FaceData} faceData
 * @property {number} trackId
 * @property {number} frameCount
 */

/**
 * @typedef {Object} TimelineBlock
 * @property {number} startTime
 * @property {number} endTime
 * @property {TimelineFaceSegment[]} segments
 */

/** 时间切片、与零比较时的容差 */
const TIME_SLICE_EPS = 1e-9;

/** 两块 `endTime` / `startTime` 是否视为时间相接（有实质间隔则不相邻） */
const TIMELINE_CONTIGUITY_EPS = 1e-5;

export const DEFAULT_MERGE_FACE_TIMELINE_OPTIONS = {
    faceAreaChangeRatioMax: 0.3, // 面积相对变化 |A1-A2|/max(A)，典型 0.1～0.5；调大：远景近景 scale 仍可同轨；调小：拒绝忽大忽小，减误并
    faceCenterOffsetHalfDiagonal: 0.45, // 人脸中心 L∞ 系数 k（相对角标量纲）；调大：快速横移仍接同一轨；调小：更严易断轨
    shortSegmentMaxDurationSec: 0.35, // 短人脸条时长(秒)阈值；小于则吸短丢弃 segments（仅相接时延后前块 end）；调大更长闪脸也被清；调小更留极短
    minFaceWidth: 0.06, // 小脸过滤宽(归一化 0～1)，与下一项为 OR：调大只要足够大的脸入轨；调小更小脸也计入
    minFaceHeight: 0.06, // 小脸过滤高(归一化 0～1)；同上，宽或高超阈即保留
    trackMissTolerance: 3, // 漏检容错帧数(整数≥0)；调大偶尔掉帧不断轨更连贯；调小易切段更碎
    mergeSameTrackGapMaxSec: 0.12, // 同一 trackId 两段若间隔≤此秒则并成一段加权框；调大短暂掉脸仍并回；调小稍断开就分两截
    faceBoxIouMin: 0.65, // 相邻帧判同轨 IoU 下限；调低抖动/缩放仍接上；调高重叠更稳、断轨增多
    faceMergeEmaAlpha: 0.58, // 轨迹内平滑参考框的当前帧权重(0～1)；近 0 更钝更稳，近 1 更紧跟更抖
    bridgeShortEmptyGapMaxSec: 0.45, // 「有人脸-短空档-同轨集合有人脸」桥接阈值(秒)；调大可并短空白；≤0 关桥接更易碎块
};

/**
 * 宽、高分别满足最小阈值才视为有效人脸（归一化 0~1）。
 */
function facePassesMinEdge(face, minFaceWidth, minFaceHeight) {
    if (!face) return false;
    const w = face.width;
    const h = face.height;
    if (!Number.isFinite(w) || !Number.isFinite(h)) return false;
    return w >= minFaceWidth || h >= minFaceHeight;
}

/** 归一化人脸框面积 width·height */
function faceNormArea(face) {
    const w = Math.max(0, face.width);
    const h = Math.max(0, face.height);
    return w * h;
}

/**
 * 两归一人脸框（轴对齐矩形）的 **IoU**：交并比 = 交集面积 / 并集面积，取值 **0～1**。
 * 坐标系与 {@link FaceData} 一致：**左上为 (x,y)**，宽高为 width/height。
 * 相邻帧同一人若有**大横移**，重叠面积小则 IoU 低，常与 `facesSimilarForMerge` 里 **`faceBoxIouMin`** 阈值搭配调参。
 *
 * @param {FaceData} a
 * @param {FaceData} b
 * @returns {number} IoU；无交集或面积为 0 时返回 0。
 */
function faceBoxIou(a, b) {
    // 重叠矩形：右下角取 min，与无重叠时宽高为负、乘积为 0 由 max(0,·) 吸收
    const x1 = Math.max(a.x, b.x);
    const y1 = Math.max(a.y, b.y);
    const x2 = Math.min(a.x + a.width, b.x + b.width);
    const y2 = Math.min(a.y + a.height, b.y + b.height);
    const inter = Math.max(0, x2 - x1) * Math.max(0, y2 - y1);
    const union = faceNormArea(a) + faceNormArea(b) - inter;
    return union > 0 ? inter / union : 0;
}

/**
 * 单框：**k × √(w²+h²)**；宽高无效时对那一侧兜底为 **k**（与 `DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceCenterOffsetHalfDiagonal` 同量纲 0～1）。
 * @param {FaceData} face
 * @param {number} k 正系数，来自选项 `faceCenterOffsetHalfDiagonal`
 */
function dynamicFaceCenterOffsetHalfDiagonal(face, k) {
    const w = face?.width;
    const h = face?.height;
    if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        return k;
    }
    return k * Math.hypot(w, h);
}

/**
 * 比对两帧：两侧各按 **k × 对角线**（无效框则 **k**）取上限再 **`max`**。
 * @param {number} k 与 `DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceCenterOffsetHalfDiagonal` 同源，由 `merge` / `advance` 传入。
 */
function pairingFaceCenterOffsetMaxForMerge(a, b, k) {
    return Math.max(dynamicFaceCenterOffsetHalfDiagonal(a, k), dynamicFaceCenterOffsetHalfDiagonal(b, k));
}

/**
 * @param {{ faceCenterOffsetHalfDiagonal?: number }} sim
 */
function resolveFaceCenterOffsetK(sim) {
    const k = sim?.faceCenterOffsetHalfDiagonal;
    return Number.isFinite(k) && k > 0 ? k : DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceCenterOffsetHalfDiagonal;
}

/**
 * 单对框：三路判定是否能「同轨迹延续」，并按 IoU↓、欧式距离↑ 贪心排序需要提供 **iou / dist**。
 * 仅用标量中心坐标，不分配 `{cx,cy}`；**IoU 只算一遍**。
 *
 * @param {FaceData} a 前一采样或参考框
 * @param {FaceData} b 当前帧框
 * @param {{ faceAreaChangeRatioMax: number, faceBoxIouMin?: number, faceCenterOffsetHalfDiagonal?: number }} sim
 * @returns {{ iou: number, dist: number } | null} 不可合并时 `null`
 */
function evaluateFacePairForMerge(a, b, sim) {
    const k = resolveFaceCenterOffsetK(sim);
    const offsetLimit = pairingFaceCenterOffsetMaxForMerge(a, b, k);
    const acx = a.x + a.width * 0.5;
    const acy = a.y + a.height * 0.5;
    const bcx = b.x + b.width * 0.5;
    const bcy = b.y + b.height * 0.5;
    const offsetLInf = Math.max(Math.abs(acx - bcx), Math.abs(acy - bcy));
    if (offsetLInf > offsetLimit) return null;

    const aa = faceNormArea(a);
    const ab = faceNormArea(b);
    const denom = Math.max(aa, ab, 1e-12);
    if (Math.abs(aa - ab) / denom > sim.faceAreaChangeRatioMax) return null;

    const iou = faceBoxIou(a, b);
    const iouMin = Number.isFinite(sim.faceBoxIouMin)
        ? sim.faceBoxIouMin
        : DEFAULT_MERGE_FACE_TIMELINE_OPTIONS.faceBoxIouMin;
    if (iou < iouMin) return null;

    const dist = Math.hypot(acx - bcx, acy - bcy);
    return { iou, dist };
}

/**
 * @param {{ faceAreaChangeRatioMax: number, faceBoxIouMin?: number, faceCenterOffsetHalfDiagonal?: number }} sim
 */
export function facesSimilarForMerge(a, b, sim) {
    return evaluateFacePairForMerge(a, b, sim) !== null;
}

/** @param {FaceData} face */
function copyFaceBox(face) {
    return { x: face.x, y: face.y, width: face.width, height: face.height, isSubject: face.isSubject };
}

/**
 * 同段内平滑参考框，减轻单帧检测抖动导致的断轨
 * @param {FaceData | null | undefined} prev
 * @param {FaceData} face
 * @param {number} alpha 0~1，越大越贴近当前帧
 */
function mergeFaceDataEma(prev, face, alpha) {
    if (!prev || !(alpha > 0)) {
        return copyFaceBox(face);
    }
    const a = Math.min(1, Math.max(0, alpha));
    const om = 1 - a;
    return {
        x: prev.x * om + face.x * a,
        y: prev.y * om + face.y * a,
        width: prev.width * om + face.width * a,
        height: prev.height * om + face.height * a,
        isSubject: prev.isSubject || face.isSubject,
    };
}

function averageFaceData(samples) {
    const n = samples.length;
    if (n === 0) {
        return {
            x: 0,
            y: 0,
            width: 0,
            height: 0,
            // mouthOpenness: 0,
        };
    }
    let sx = 0;
    let sy = 0;
    let sw = 0;
    let sh = 0;
    // let sm = 0;
    for (const f of samples) {
        sx += f.x;
        sy += f.y;
        sw += f.width;
        sh += f.height;
        // sm += f.mouthOpenness;
    }
    return {
        x: sx / n,
        y: sy / n,
        width: sw / n,
        height: sh / n,
        // mouthOpenness: sm / n,
        isSubject: samples.some(f => f.isSubject),
    };
}

//-----------------------------------------------------------------------------
// §C 单帧轨迹匹配：`assignTracksToFrame` + 与 merge/advance 共用的接续逻辑
//-----------------------------------------------------------------------------

/** 人脸水平中心 x（用于多人场景下确定性轨道遍历顺序） */
function centerX(f) {
    return f.x + f.width / 2;
}

/**
 * IoU 优先、欧氏距离次之的贪心：**每个 (轨迹,候选脸) 对只评估一次**，避免重复 IoU。
 * @returns {Map<number, number>} trackId → currFaces 下标
 */
function assignTracksToFrame(currFaces, lastFaceByTrack, similarityMergeOpts) {
    const trackToFaceIndex = new Map();
    const usedFace = new Set();

    const trackIds = [...lastFaceByTrack.keys()].sort(
        (a, b) => centerX(lastFaceByTrack.get(a)) - centerX(lastFaceByTrack.get(b)),
    );

    /** @type {{ tid: number, ci: number, iou: number, d: number }[]} */
    const candidates = [];
    for (const tid of trackIds) {
        const last = lastFaceByTrack.get(tid);
        for (let ci = 0; ci < currFaces.length; ci++) {
            const cf = currFaces[ci];
            const m = evaluateFacePairForMerge(last, cf, similarityMergeOpts);
            if (!m) continue;
            candidates.push({
                tid,
                ci,
                iou: m.iou,
                d: m.dist,
            });
        }
    }
    candidates.sort((a, b) => (b.iou !== a.iou ? b.iou - a.iou : a.d - b.d));

    for (const c of candidates) {
        if (trackToFaceIndex.has(c.tid) || usedFace.has(c.ci)) continue;
        trackToFaceIndex.set(c.tid, c.ci);
        usedFace.add(c.ci);
    }

    return trackToFaceIndex;
}

/**
 * 本帧：续接已有轨并为未匹配的检测新开 id；写入 `tracks` 中新轨占位。**merge/advance 复用**，避免分叉两套循环。
 *
 * @param {FaceData[]} faces 已按短边阈值过滤的脸
 * @param {Map<number, { lastFace: FaceData, open?: *, missCount?: number, mergeRefFace?: * }>} tracks
 * @param {{ faceAreaChangeRatioMax: number, faceBoxIouMin?: number, faceCenterOffsetHalfDiagonal?: number }} similarityMergeOpts
 * @param {number} nextTrackId 下一可用整数 id
 */
function attachCurrentFrameFacesToTracks(faces, tracks, similarityMergeOpts, nextTrackId) {
    const lastFaceByTrack = new Map();
    for (const [tid, tr] of tracks) {
        lastFaceByTrack.set(tid, tr.lastFace);
    }

    const assignedFromPrev = assignTracksToFrame(faces, lastFaceByTrack, similarityMergeOpts);
    const usedIndices = new Set(assignedFromPrev.values());

    const thisFrameByTrack = new Map();
    for (const [tid, idx] of assignedFromPrev) {
        thisFrameByTrack.set(tid, faces[idx]);
    }

    let nid = nextTrackId;
    for (let i = 0; i < faces.length; i++) {
        if (usedIndices.has(i)) continue;
        const tid = nid++;
        const f = faces[i];
        tracks.set(tid, { lastFace: f, open: null, missCount: 0, mergeRefFace: null });
        thisFrameByTrack.set(tid, f);
    }

    return {
        thisFrameByTrack,
        assignedTrackIds: new Set(thisFrameByTrack.keys()),
        nextTrackId: nid,
    };
}

function filterFacesByMinEdge(row, minFaceWidth, minFaceHeight) {
    const raw = Array.isArray(row.facesData) ? row.facesData : [];
    return raw.filter(f => facePassesMinEdge(f, minFaceWidth, minFaceHeight));
}

//-----------------------------------------------------------------------------
// § 导出：小脸过滤、轨迹状态、单帧 advance（行为与下方 merge 主循环一致）
//-----------------------------------------------------------------------------

/**
 * 与 merge 一致的小脸过滤，供解码采样侧单帧轨迹判断使用。
 * @param {FaceData[]} faces
 * @param {number} minFaceWidth
 * @param {number} minFaceHeight
 * @returns {FaceData[]}
 */
export function filterFacesArrayByMinEdge(faces, minFaceWidth, minFaceHeight) {
    if (!Array.isArray(faces)) return [];
    return faces.filter(f => facePassesMinEdge(f, minFaceWidth, minFaceHeight));
}

/** 与 mergeFramesIntoSegments 内部一致的轨迹状态（仅用于采样端与 merge 对齐的 track 判断） */
export function createMergeFrameTrackState() {
    return { nextTrackId: 0, tracks: new Map() };
}

/** 深拷贝轨迹状态，供试探性 advance 不污染正式状态 */
export function cloneMergeFrameTrackState(state) {
    const tracks = new Map();
    for (const [tid, tr] of state.tracks) {
        const open = tr.open
            ? {
                  startTime: tr.open.startTime,
                  endTime: tr.open.endTime,
                  samples: tr.open.samples.map(s => ({ ...s })),
              }
            : null;
        tracks.set(tid, {
            lastFace: { ...tr.lastFace },
            open,
            missCount: tr.missCount ?? 0,
            mergeRefFace: tr.mergeRefFace ? { ...tr.mergeRefFace } : null,
        });
    }
    return { nextTrackId: state.nextTrackId, tracks };
}

/**
 * 解码单帧预览：与 {@link mergeFramesIntoSegments} **单步同构**；不写入 `MergedFaceSegment[]`，仅更新 state 并返回本帧 `trackIdsKey` 与有效脸数。
 *
 * @param {number} currentTime
 * @param {FaceData[]} rawFaces detectFrame 原始输出
 * @param {{ nextTrackId: number, tracks: Map<number, { lastFace: FaceData, open: { startTime: number, endTime: number, samples: FaceData[] } | null }> }} state
 * @param {Partial<MergeFaceTimelineOptions>} [options]
 * @returns {{ trackIdsKey: string, faceCount: number }}
 */
export function advanceMergeTrackStateForRow(currentTime, rawFaces, state, options = {}) {
    const opts = { ...DEFAULT_MERGE_FACE_TIMELINE_OPTIONS, ...options };

    const {
        faceAreaChangeRatioMax,
        minFaceWidth,
        minFaceHeight,
        trackMissTolerance,
        faceBoxIouMin,
        faceMergeEmaAlpha,
        faceCenterOffsetHalfDiagonal,
    } = opts;
    const similarityMergeOpts = { faceAreaChangeRatioMax, faceBoxIouMin, faceCenterOffsetHalfDiagonal };
    const faces = filterFacesArrayByMinEdge(rawFaces, minFaceWidth, minFaceHeight);
    const t = currentTime;
    const { tracks } = state;
    let { nextTrackId } = state;
    const missTol = Math.max(0, Math.floor(trackMissTolerance));

    const flushOpen = trackId => {
        const tr = tracks.get(trackId);
        if (tr) {
            tr.open = null;
            tr.missCount = 0;
            tr.mergeRefFace = null;
        }
    };

    const closeUnassignedTracks = assignedTrackIds => {
        for (const [tid, tr] of tracks) {
            if (tr.open && !assignedTrackIds.has(tid)) {
                tr.missCount = (tr.missCount ?? 0) + 1;
                if (tr.missCount > missTol) {
                    flushOpen(tid);
                }
            } else if (assignedTrackIds.has(tid)) {
                tr.missCount = 0;
            }
        }
    };

    const closeAllOpen = () => {
        for (const [tid, tr] of tracks) {
            if (!tr.open) continue;
            tr.missCount = (tr.missCount ?? 0) + 1;
            if (tr.missCount > missTol) {
                flushOpen(tid);
            }
        }
    };

    if (faces.length === 0) {
        closeAllOpen();
        state.nextTrackId = nextTrackId;
        return { trackIdsKey: '', faceCount: 0 };
    }

    const resolved = attachCurrentFrameFacesToTracks(faces, tracks, similarityMergeOpts, nextTrackId);
    nextTrackId = resolved.nextTrackId;
    const { thisFrameByTrack, assignedTrackIds } = resolved;

    for (const [tid, face] of thisFrameByTrack) {
        let tr = tracks.get(tid);
        if (!tr) {
            tr = { lastFace: face, open: null, missCount: 0, mergeRefFace: null };
            tracks.set(tid, tr);
        }

        if (!tr.open) {
            tr.open = { startTime: t, endTime: t, samples: [face] };
            tr.mergeRefFace = copyFaceBox(face);
        } else {
            const ref =
                faceMergeEmaAlpha > 0 && tr.mergeRefFace
                    ? tr.mergeRefFace
                    : tr.open.samples[tr.open.samples.length - 1];
            if (facesSimilarForMerge(ref, face, similarityMergeOpts)) {
                tr.open.endTime = t;
                tr.open.samples.push(face);
                if (faceMergeEmaAlpha > 0) {
                    tr.mergeRefFace = mergeFaceDataEma(tr.mergeRefFace, face, faceMergeEmaAlpha);
                }
            } else {
                flushOpen(tid);
                tr.open = { startTime: t, endTime: t, samples: [face] };
                tr.mergeRefFace = copyFaceBox(face);
            }
        }
        tr.lastFace = face;
        tr.missCount = 0;
    }

    closeUnassignedTracks(assignedTrackIds);

    state.nextTrackId = nextTrackId;
    const trackIdsKey = [...thisFrameByTrack.keys()].sort((a, b) => a - b).join(',');
    return { trackIdsKey, faceCount: faces.length };
}

/**
 * @returns {{ timeMin: number, timeMax: number } | null}
 */
function getFrameTimeBounds(frames) {
    let timeMin = NaN;
    let timeMax = NaN;
    for (const row of frames) {
        const ct = row.currentTime;
        if (!Number.isFinite(ct)) continue;
        if (!Number.isFinite(timeMin)) {
            timeMin = timeMax = ct;
        } else {
            timeMin = Math.min(timeMin, ct);
            timeMax = Math.max(timeMax, ct);
        }
    }
    if (!Number.isFinite(timeMin)) return null;
    return { timeMin, timeMax };
}

//-----------------------------------------------------------------------------
// §E 全时序：按解码时间推进各轨，产出 `MergedFaceSegment[]` 并压缩同 id 微隙
//-----------------------------------------------------------------------------

/**
 * 阶段一：**整段视频**按时间顺序逐帧推进「开口」轨迹，末帧收口后与 `advanceMergeTrackStateForRow` **单步逻辑一致**（但此处会 `flushOpen` 写入 `merged`）。
 *
 * @param {FrameFaceRow[]} frames **已按 currentTime 排序**（入口处会再 sort 一次保证稳定）
 * @param {MergeFaceTimelineOptions & typeof DEFAULT_MERGE_FACE_TIMELINE_OPTIONS} timelineOpts
 * @returns {MergedFaceSegment[]}
 */
function mergeFramesIntoSegments(frames, timelineOpts) {
    const {
        faceAreaChangeRatioMax,
        faceBoxIouMin,
        faceMergeEmaAlpha,
        faceCenterOffsetHalfDiagonal,
        minFaceWidth,
        minFaceHeight,
        trackMissTolerance,
        mergeSameTrackGapMaxSec,
    } = timelineOpts;
    const similarityMergeOpts = { faceAreaChangeRatioMax, faceBoxIouMin, faceCenterOffsetHalfDiagonal };
    const missTol = Math.max(0, Math.floor(trackMissTolerance));
    const gapMergeSec = Math.max(0, mergeSameTrackGapMaxSec);

    /** @type {MergedFaceSegment[]} */
    const merged = [];

    /** @type {Map<number, { lastFace: FaceData, open: { startTime: number, endTime: number, samples: FaceData[] } | null, missCount?: number, mergeRefFace?: FaceData | null }>} */
    const tracks = new Map();
    let nextTrackId = 0;

    const flushOpen = (trackId, open) => {
        merged.push({
            faceData: averageFaceData(open.samples),
            startTime: open.startTime,
            endTime: open.endTime,
            frameCount: open.samples.length,
            trackId,
        });
        const tr = tracks.get(trackId);
        if (tr) {
            tr.open = null;
            tr.missCount = 0;
            tr.mergeRefFace = null;
        }
    };

    const closeUnassignedTracks = assignedTrackIds => {
        for (const [tid, tr] of tracks) {
            if (tr.open && !assignedTrackIds.has(tid)) {
                tr.missCount = (tr.missCount ?? 0) + 1;
                if (tr.missCount > missTol) {
                    flushOpen(tid, tr.open);
                }
            } else if (assignedTrackIds.has(tid)) {
                tr.missCount = 0;
            }
        }
    };

    const closeAllOpen = () => {
        for (const [tid, tr] of tracks) {
            if (tr.open) {
                tr.missCount = (tr.missCount ?? 0) + 1;
                // 漏检尚未超过 missTol：暂不收口，给下一帧一次续接机会
                if (tr.missCount <= missTol) continue;
                flushOpen(tid, tr.open);
            }
        }
    };

    // 主循环：每帧先 `attachCurrentFrameFacesToTracks`，再延长/截断各轨开口段
    for (const row of frames) {
        const t = row.currentTime;
        const faces = filterFacesByMinEdge(row, minFaceWidth, minFaceHeight);

        if (faces.length === 0) {
            closeAllOpen();
            continue;
        }

        const resolved = attachCurrentFrameFacesToTracks(faces, tracks, similarityMergeOpts, nextTrackId);
        nextTrackId = resolved.nextTrackId;
        const { thisFrameByTrack, assignedTrackIds } = resolved;

        for (const [tid, face] of thisFrameByTrack) {
            let tr = tracks.get(tid);
            if (!tr) {
                tr = { lastFace: face, open: null, missCount: 0, mergeRefFace: null };
                tracks.set(tid, tr);
            }

            if (!tr.open) {
                tr.open = { startTime: t, endTime: t, samples: [face] };
                tr.mergeRefFace = copyFaceBox(face);
            } else {
                const ref =
                    faceMergeEmaAlpha > 0 && tr.mergeRefFace
                        ? tr.mergeRefFace
                        : tr.open.samples[tr.open.samples.length - 1];
                if (facesSimilarForMerge(ref, face, similarityMergeOpts)) {
                    tr.open.endTime = t;
                    tr.open.samples.push(face);
                    if (faceMergeEmaAlpha > 0) {
                        tr.mergeRefFace = mergeFaceDataEma(tr.mergeRefFace, face, faceMergeEmaAlpha);
                    }
                } else {
                    flushOpen(tid, tr.open);
                    tr.open = { startTime: t, endTime: t, samples: [face] };
                    tr.mergeRefFace = copyFaceBox(face);
                }
            }
            tr.lastFace = face;
            tr.missCount = 0;
        }

        closeUnassignedTracks(assignedTrackIds);
    }

    for (const [tid, tr] of tracks) {
        if (tr.open) {
            flushOpen(tid, tr.open);
        }
    }

    merged.sort((a, b) => (a.startTime !== b.startTime ? a.startTime - b.startTime : a.trackId - b.trackId));
    if (merged.length <= 1) return merged;

    /** @type {MergedFaceSegment[]} */
    const compactMerged = [];
    // 同一 trackId、时间上间隔 ≤ gapMergeSec：视为同一段分段的小断裂，首尾加权合并框
    for (const seg of merged) {
        const last = compactMerged[compactMerged.length - 1];
        if (last && last.trackId === seg.trackId && seg.startTime - last.endTime <= gapMergeSec) {
            const lastFrames = Math.max(1, last.frameCount);
            const segFrames = Math.max(1, seg.frameCount);
            last.faceData = mergeFaceDataWeighted(last.faceData, lastFrames, seg.faceData, segFrames);
            last.endTime = seg.endTime;
            last.frameCount += seg.frameCount;
        } else {
            compactMerged.push({ ...seg, faceData: { ...seg.faceData } });
        }
    }
    return compactMerged;
}

function mergeFaceDataWeighted(a, wA, b, wB) {
    const w = wA + wB;
    if (w <= 0) {
        return { ...a };
    }
    return {
        x: (a.x * wA + b.x * wB) / w,
        y: (a.y * wA + b.y * wB) / w,
        width: (a.width * wA + b.width * wB) / w,
        height: (a.height * wA + b.height * wB) / w,
        isSubject: a.isSubject || b.isSubject,
        // mouthOpenness: (a.mouthOpenness * wA + b.mouthOpenness * wB) / w,
    };
}

//-----------------------------------------------------------------------------
// §F 时间条带：在全部段端点处切片，使每 strip 内各轨时间上可并列展示
//-----------------------------------------------------------------------------

function partitionMergedSegmentsToNonOverlappingBlocks(merged) {
    if (!merged.length) {
        return [];
    }

    /** @type {Set<number>} */
    const bounds = new Set();
    for (const s of merged) {
        bounds.add(s.startTime);
        bounds.add(s.endTime);
    }
    const sorted = [...bounds].sort((x, y) => x - y);

    /** @type {TimelineBlock[]} */
    const strips = [];

    // 相邻时间点 [a,b) 为基本条；对每个 strip 枚举所有与该区间相交的 merged 段并分摊 frameCount
    for (let k = 0; k < sorted.length - 1; k++) {
        const a = sorted[k];
        const b = sorted[k + 1];
        if (b - a <= TIME_SLICE_EPS) {
            continue;
        }

        /** @type {TimelineFaceSegment[]} */
        const segments = [];

        for (const s of merged) {
            if (s.startTime < b && s.endTime > a) {
                const stripDur = b - a;
                const segDur = s.endTime - s.startTime;
                let fc = s.frameCount;
                if (segDur > TIME_SLICE_EPS) {
                    fc = Math.max(1, Math.round((s.frameCount * stripDur) / segDur));
                }
                segments.push({
                    faceData: { ...s.faceData },
                    trackId: s.trackId,
                    frameCount: fc,
                });
            }
        }

        if (segments.length) {
            segments.sort((x, y) => x.trackId - y.trackId);
            strips.push({
                startTime: a,
                endTime: b,
                segments,
            });
        }
    }

    return strips;
}

function trackIdsKey(block) {
    return block.segments
        .map(s => s.trackId)
        .sort((x, y) => x - y)
        .join(',');
}

function mergeConsecutiveBlocksWithSameTrackSet(blocks) {
    if (!blocks.length) {
        return [];
    }

    /** @type {TimelineBlock[]} */
    const out = [];

    let cur = {
        startTime: blocks[0].startTime,
        endTime: blocks[0].endTime,
        segments: blocks[0].segments.map(s => ({
            faceData: { ...s.faceData },
            trackId: s.trackId,
            frameCount: s.frameCount,
        })),
    };

    for (let i = 1; i < blocks.length; i++) {
        const next = blocks[i];
        const contiguous = next.startTime <= cur.endTime + TIMELINE_CONTIGUITY_EPS;
        if (trackIdsKey(cur) === trackIdsKey(next) && contiguous) {
            cur.endTime = next.endTime;
            const byTrack = new Map(cur.segments.map(s => [s.trackId, s]));
            for (const ns of next.segments) {
                const ex = byTrack.get(ns.trackId);
                if (ex) {
                    const w1 = ex.frameCount;
                    const w2 = ns.frameCount;
                    ex.faceData = mergeFaceDataWeighted(ex.faceData, w1, ns.faceData, w2);
                    ex.frameCount = w1 + w2;
                }
            }
            cur.segments.sort((x, y) => x.trackId - y.trackId);
        } else {
            out.push(cur);
            cur = {
                startTime: next.startTime,
                endTime: next.endTime,
                segments: next.segments.map(s => ({
                    faceData: { ...s.faceData },
                    trackId: s.trackId,
                    frameCount: s.frameCount,
                })),
            };
        }
    }
    out.push(cur);
    return out;
}

function buildTimelineBlocksFromMerged(merged) {
    const strips = partitionMergedSegmentsToNonOverlappingBlocks(merged);
    return mergeConsecutiveBlocksWithSameTrackSet(strips);
}

/** 深拷贝块内每条 `TimelineFaceSegment`（调用方不希望与上游共享引用时） */
function cloneTimelineFaceSegments(segments) {
    if (!Array.isArray(segments)) return [];
    return segments.map(s => ({
        faceData: { ...s.faceData },
        trackId: s.trackId,
        frameCount: s.frameCount,
    }));
}

//-----------------------------------------------------------------------------
// §G TimelineBlock 后处理：短段吸收 · 填空档 · 桥接 · 相邻合并
//-----------------------------------------------------------------------------

/**
 * 短人脸块：丢弃 segments；仅在与前一块**时间相接**时并到前一块 endTime。
 * 不相邻时保留为独立 `{ startTime, endTime, segments: [] }`，避免把中间空档算进上一段人脸。
 */
function absorbShortBlocksIntoPrevious(blocks, shortSegmentMaxDurationSec) {
    if (!Array.isArray(blocks) || blocks.length === 0 || !(shortSegmentMaxDurationSec > 0)) {
        return blocks;
    }

    /** @type {TimelineBlock[]} */
    const out = [];

    for (const block of blocks) {
        const dur = block.endTime - block.startTime;
        const isShort = dur + TIME_SLICE_EPS < shortSegmentMaxDurationSec;
        const prev = out.length > 0 ? out[out.length - 1] : null;
        const contiguousToPrev = prev && block.startTime <= prev.endTime + TIMELINE_CONTIGUITY_EPS;

        if (isShort) {
            if (prev && contiguousToPrev) {
                prev.endTime = Math.max(prev.endTime, block.endTime);
                continue;
            }
            out.push({
                startTime: block.startTime,
                endTime: block.endTime,
                segments: [],
            });
            continue;
        }

        out.push({
            startTime: block.startTime,
            endTime: block.endTime,
            segments: cloneTimelineFaceSegments(block.segments),
        });
    }

    return out;
}

/**
 * 在 [timeMin, timeMax] 内按时间排序遍历**已有块**（含 `segments: []`），块与块之间的空隙补空段。
 * 假定各块时间区间互不重叠（partition + 相邻合并后通常满足）。
 */
export function injectEmptyTimelineGaps(blocks, timeMin, timeMax) {
    if (!Number.isFinite(timeMin) || !Number.isFinite(timeMax) || timeMax - timeMin <= TIME_SLICE_EPS) {
        return blocks;
    }

    const sorted = blocks
        .filter(b => b && Number.isFinite(b.startTime) && Number.isFinite(b.endTime))
        .slice()
        .sort((a, b) => (a.startTime !== b.startTime ? a.startTime - b.startTime : a.endTime - b.endTime));

    if (sorted.length === 0) {
        return [{ startTime: timeMin, endTime: timeMax, segments: [] }];
    }

    /** @type {TimelineBlock[]} */
    const out = [];
    let cursor = timeMin;

    for (const b of sorted) {
        const bs = Math.max(b.startTime, timeMin);
        const be = Math.min(b.endTime, timeMax);
        if (be - bs <= TIME_SLICE_EPS) {
            continue;
        }

        if (bs > cursor + TIME_SLICE_EPS) {
            out.push({ startTime: cursor, endTime: bs, segments: [] });
        }
        const blockStart = Math.max(bs, cursor);
        out.push({
            startTime: blockStart,
            endTime: be,
            segments: cloneTimelineFaceSegments(b.segments),
        });
        cursor = Math.max(cursor, be);
    }

    if (cursor < timeMax - TIME_SLICE_EPS) {
        out.push({ startTime: cursor, endTime: timeMax, segments: [] });
    }

    return out;
}

/**
 * 前后两段轨迹签名一致、中间为短无人脸间隙时合并，减轻检测闪断导致的构图切换
 * @param {TimelineBlock[]} blocks
 * @param {number} bridgeMaxSec
 * @returns {TimelineBlock[]}
 */
function bridgeShortEmptyGapsBetweenSameTracks(blocks, bridgeMaxSec) {
    if (!Array.isArray(blocks) || blocks.length < 3 || !(bridgeMaxSec > 0)) {
        return blocks;
    }

    /** @type {TimelineBlock[]} */
    const out = [];
    let i = 0;
    while (i < blocks.length) {
        const a = blocks[i];
        const b = blocks[i + 1];
        const c = blocks[i + 2];
        if (
            i + 2 < blocks.length &&
            Array.isArray(a.segments) &&
            a.segments.length > 0 &&
            Array.isArray(b.segments) &&
            b.segments.length === 0 &&
            Array.isArray(c.segments) &&
            c.segments.length > 0 &&
            trackIdsKey(a) === trackIdsKey(c) &&
            b.endTime - b.startTime <= bridgeMaxSec + TIME_SLICE_EPS &&
            b.startTime <= a.endTime + TIMELINE_CONTIGUITY_EPS &&
            c.startTime <= b.endTime + TIMELINE_CONTIGUITY_EPS
        ) {
            const wA = Math.max(TIME_SLICE_EPS, a.endTime - a.startTime);
            const wC = Math.max(TIME_SLICE_EPS, c.endTime - c.startTime);
            const mergedSegs = a.segments.map(s => ({
                faceData: { ...s.faceData },
                trackId: s.trackId,
                frameCount: s.frameCount,
            }));
            const byTrack = new Map(mergedSegs.map(s => [s.trackId, s]));
            for (const sc of c.segments) {
                const ex = byTrack.get(sc.trackId);
                if (ex) {
                    ex.faceData = mergeFaceDataWeighted(ex.faceData, wA, sc.faceData, wC);
                    ex.frameCount = ex.frameCount + sc.frameCount;
                }
            }
            mergedSegs.sort((x, y) => x.trackId - y.trackId);
            out.push({
                startTime: a.startTime,
                endTime: c.endTime,
                segments: mergedSegs,
            });
            i += 3;
        } else {
            out.push(blocks[i]);
            i += 1;
        }
    }
    return out;
}

//-----------------------------------------------------------------------------
// §G-stable 末尾：按 stableFragments 将条带聚合为「每片段一块」
//-----------------------------------------------------------------------------

/**
 * 将管线末尾的 `TimelineBlock[]` 按稳定片段合并：每片段至多一块；片内条带聚合。
 * 块归属片段：取时间中点 `(start+end)/2` 落入的片段。
 *
 * @param {TimelineBlock[]} blocks
 * @param {{ startTime: number, endTime: number }[]} fragments 预归一化 stableFragments
 * @param {number} timeMin
 * @param {number} timeMax
 * @returns {TimelineBlock[]}
 */
function mergeTimelineBlocksByStableFragments(blocks, fragments, timeMin, timeMax) {
    if (!Array.isArray(blocks) || blocks.length === 0 || fragments.length === 0) {
        return blocks;
    }
    const eps = 0.5;
    const newFragments = fragments.filter(f => f.endTime > timeMin && f.startTime < timeMax);
    const floatFragments = Array.from(new Set(newFragments.map(i => [i.startTime, i.endTime]).flat()));
    floatFragments[0] = timeMin;
    floatFragments[floatFragments.length - 1] = timeMax;
    const floatBlocks = Array.from(new Set(blocks.map(i => [i.startTime, i.endTime]).flat()));

    const newFloat = floatFragments.map(f => {
        const diffArr = floatBlocks.map(b => Math.abs(f - b));
        const minDiff = Math.min(...diffArr);
        if (minDiff <= eps) {
            const idx = diffArr.indexOf(Math.min(...diffArr));
            return floatBlocks[idx];
        }
        return f;
    });
    const dupFloat = Array.from(new Set(newFloat)).sort((a, b) => a - b);
    const newBlocksTimes = dupFloat
        .map((nf, idx) => (!dupFloat[idx + 1] ? null : { startTime: nf, endTime: dupFloat[idx + 1] }))
        .filter(i => !!i);
    const out = [];
    newBlocksTimes.forEach(b => {
        const brockSeg = blocks.filter(item => !(item.endTime <= b.startTime || item.startTime >= b.endTime));
        const isSubject = brockSeg.every(i => i.segments.every(s => s.faceData.isSubject));
        const __getMinAreaSegments = seg => {
            return seg.reduce(
                (c, s) => {
                    const area = s.faceData.width * s.faceData.height;
                    return c < area ? c : area;
                },
                seg.length > 0 ? seg[0].faceData.width * seg[0].faceData.height : 0,
            );
        };
        if (isSubject) {
            // todo
            const segments = brockSeg.reduce((calc, cur) => {
                //返回面积最小的片段
                const segmentArea = __getMinAreaSegments(cur.segments);
                const calcArea = __getMinAreaSegments(calc);
                return calcArea > segmentArea && calcArea > 0 ? calc : cur.segments;
            }, []);
            out.push({ ...b, segments: segments });
        } else {
            const segments = brockSeg.reduce((calc, cur) => {
                return (cur.segments.length && !!cur.segments.find(s => !s.faceData.isSubject)) > calc.length
                    ? cur.segments.map(i => ({ faceData: i.faceData, trackId: i.trackId }))
                    : calc;
            }, []);
            out.push({ ...b, segments: segments });
        }
    });
    out.sort((a, b) => (a.startTime !== b.startTime ? a.startTime - b.startTime : a.endTime - b.endTime));

    return injectEmptyTimelineGaps(out, timeMin, timeMax);
}

/**
 * 主入口：抽样帧 → **`TimelineBlock[]`**（首尾时间域内无漏段，短段/空档按 options 处理）。
 *
 * **固定管线**：排序与边界 → 帧级合并 → 拆条带 → 填空档 → 吸短段 → 短空桥接 → 相邻同轨再并；若传入 **`stableFragments`**（`{ startTime, endTime }[]`，由上游预归一化）则**最后再**按片段时间窗聚合成「每片段一块」并重新填空档。
 *
 * @param {FrameFaceRow[]} data
 * @param {Partial<MergeFaceTimelineOptions>} [options] 覆盖默认阈值
 * @returns {TimelineBlock[]}
 */
export default function mergeFaceDetectionTimeline(data, options = {}) {
    if (!Array.isArray(data) || data.length === 0) {
        return [];
    }

    const opts = { ...DEFAULT_MERGE_FACE_TIMELINE_OPTIONS, ...options };

    const { shortSegmentMaxDurationSec, bridgeShortEmptyGapMaxSec } = opts;

    // 复制后排序，避免破坏调用方数组且保证时间单调
    const frames = [...data].sort((a, b) => a.currentTime - b.currentTime);
    const stableCuts = opts.stableFragments || [];

    const bounds = getFrameTimeBounds(frames);
    if (!bounds) {
        return [];
    }

    const { timeMin, timeMax } = bounds;
    //接续轨迹块
    const merged = mergeFramesIntoSegments(frames, opts);
    // 按合并规则构建时间块
    let blocks = buildTimelineBlocksFromMerged(merged);
    // 补空时间段
    blocks = injectEmptyTimelineGaps(blocks, timeMin, timeMax);
    // 吸短时间段
    blocks = absorbShortBlocksIntoPrevious(blocks, shortSegmentMaxDurationSec);
    // 桥接短空时间段
    blocks = bridgeShortEmptyGapsBetweenSameTracks(blocks, bridgeShortEmptyGapMaxSec);
    // 按稳定片段合并
    if (stableCuts.length > 0) {
        blocks = mergeTimelineBlocksByStableFragments(blocks, stableCuts, timeMin, timeMax);
    } else {
        // 桥接 / 吸短后可能再出现可并的相邻同集合条
        blocks = mergeConsecutiveBlocksWithSameTrackSet(blocks);
    }

    return blocks;
}
